/** Client mirror of server/promotion-schedule.js (Jordan / Asia/Amman). */

import { isQualifyingDrinkMenuItem, isQualifyingFoodMenuItem } from '../constants/points';

const PROMO_TIMEZONE = 'Asia/Amman';

function parseTimeToMinutes(hhmm: string): number | null {
  const m = hhmm.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return h * 60 + min;
}

function formatMinutesAsHhmm(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function normalizeMeridian(value: string | null | undefined): 'am' | 'pm' | null {
  if (!value) return null;
  const key = String(value).replace(/\./g, '').toLowerCase().trim();
  return key === 'am' || key === 'pm' ? key : null;
}

function minutesFromHourMinute(
  hour: number | string,
  minute: number | string,
  meridian?: string | null
): number | null {
  let h = Number(hour);
  const min = Number(minute || 0);
  if (!Number.isFinite(h) || !Number.isFinite(min) || min < 0 || min > 59) return null;
  const mer = normalizeMeridian(meridian);
  if (mer) {
    if (h < 1 || h > 12) return null;
    if (mer === 'am') {
      if (h === 12) h = 0;
    } else if (h !== 12) {
      h += 12;
    }
  } else if (h < 0 || h > 23) {
    return null;
  }
  return h * 60 + min;
}

function parseFlexibleClock(value: string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const raw = String(value).trim();
  const hhmm = parseTimeToMinutes(raw);
  if (hhmm != null) return hhmm;
  const m = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?)$/i);
  if (!m) return null;
  return minutesFromHourMinute(m[1], m[2] || 0, m[3]);
}

export function extractDailyWindowFromText(
  text: string | null | undefined
): { start: string; end: string } | null {
  if (!text || typeof text !== 'string') return null;
  const source = text.replace(/\u00a0/g, ' ');
  const re =
    /(?:\b(?:between|from|during)\s+)?(\d{1,2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?)?\s*(?:[-–—]|to|until|till)\s*(\d{1,2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?)?/gi;
  let match: RegExpExecArray | null;
  while ((match = re.exec(source))) {
    const full = match[0];
    const hasMeridian = /a\.?m\.?|p\.?m\.?/i.test(full);
    const hasColon = Boolean(match[2] || match[5]);
    const hasCue = /(?:between|from|during)/i.test(full);
    if (!hasMeridian && !hasColon && !hasCue) continue;

    const startH = Number(match[1]);
    const startMin = match[2] != null ? Number(match[2]) : 0;
    let startMer = normalizeMeridian(match[3]);
    const endH = Number(match[4]);
    const endMin = match[5] != null ? Number(match[5]) : 0;
    let endMer = normalizeMeridian(match[6]);

    let startMins: number | null;
    let endMins: number | null;
    if (!startMer && !endMer) {
      startMins = minutesFromHourMinute(startH, startMin, null);
      endMins = minutesFromHourMinute(endH, endMin, null);
      if (startMins == null || endMins == null) continue;
      if (!hasColon && startH <= 12 && endH <= 12) {
        startMins = minutesFromHourMinute(startH, startMin, 'am');
        endMins = minutesFromHourMinute(endH, endMin, endH === 12 ? 'pm' : 'am');
      }
    } else {
      if (startMer && !endMer) {
        endMer = endH === 12 && startMer === 'am' ? 'pm' : startMer;
      } else if (!startMer && endMer) {
        if (startH === 12 && endMer === 'pm') startMer = 'am';
        else if (startH > endH && endMer === 'pm') startMer = 'am';
        else startMer = endMer;
      }
      startMins = minutesFromHourMinute(startH, startMin, startMer);
      endMins = minutesFromHourMinute(endH, endMin, endMer);
    }
    if (startMins == null || endMins == null) continue;
    return { start: formatMinutesAsHhmm(startMins), end: formatMinutesAsHhmm(endMins) };
  }
  return null;
}

export function resolveDailyHours(promo: {
  daily_start_time?: string | null;
  daily_end_time?: string | null;
  title?: string | null;
  description?: string | null;
}): { start: string; end: string } | null {
  const start = normalizeDailyTime(promo.daily_start_time);
  const end = normalizeDailyTime(promo.daily_end_time);
  if (start && end) return { start, end };
  return extractDailyWindowFromText([promo.title, promo.description].filter(Boolean).join(' '));
}

function getAmmanMinutesOfDay(date = new Date()): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: PROMO_TIMEZONE,
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  }).formatToParts(date);
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
  return hour * 60 + minute;
}

export function normalizeDailyTime(value: string | null | undefined): string | null {
  if (value == null || value === '') return null;
  const mins = parseFlexibleClock(value);
  if (mins == null) return null;
  return formatMinutesAsHhmm(mins);
}

export function isWithinDailyWindow(
  promo: {
    daily_start_time?: string | null;
    daily_end_time?: string | null;
    title?: string | null;
    description?: string | null;
  },
  now = new Date()
): boolean {
  const dailyWindow = resolveDailyHours(promo);
  if (!dailyWindow) return true;
  const start = dailyWindow.start;
  const end = dailyWindow.end;

  const nowM = getAmmanMinutesOfDay(now);
  const startM = parseTimeToMinutes(start);
  const endM = parseTimeToMinutes(end);
  if (startM == null || endM == null) return true;

  if (startM < endM) return nowM >= startM && nowM <= endM;
  if (startM > endM) return nowM >= startM || nowM <= endM;
  return true;
}

export function isPromotionActiveNow(
  promo: {
    active?: boolean | number;
    valid_from: string;
    valid_until: string;
    daily_start_time?: string | null;
    daily_end_time?: string | null;
    title?: string | null;
    description?: string | null;
  },
  now = new Date()
): boolean {
  if (!promo || !promo.active) return false;
  const t = now.getTime();
  const from = new Date(promo.valid_from).getTime();
  const until = new Date(promo.valid_until).getTime();
  if (Number.isNaN(from) || Number.isNaN(until)) return false;
  if (t < from || t > until) return false;
  return isWithinDailyWindow(promo, now);
}

export type PromoDeal = {
  promotion_type?: string | null;
  deal_type?: string | null;
  bogo_off_percent?: number | null;
  discount_percentage?: number;
  discount_amount?: number;
  campaign_audience?: string | null;
  campaign_placement?: string | null;
  offer_applies_to?: string | null;
};

export type PromoLineItem = {
  quantity?: number;
  price?: number;
  unit_price?: number;
  per_unit_price?: number;
  category?: string | null;
  name?: string | null;
};

export function normalizeOfferAppliesTo(value?: string | null): 'all' | 'drinks' | 'food' {
  const key = String(value || 'all').toLowerCase().trim();
  if (key === 'drinks') return 'drinks';
  if (key === 'food') return 'food';
  return 'all';
}

export function offerAppliesToDrinks(promo: PromoDeal | null | undefined): boolean {
  return isOfferPromotion(promo) && normalizeOfferAppliesTo(promo?.offer_applies_to) === 'drinks';
}

function itemQualifiesForOffer(
  promo: PromoDeal | null | undefined,
  item: PromoLineItem
): boolean {
  const applies = isOfferPromotion(promo) ? normalizeOfferAppliesTo(promo?.offer_applies_to) : 'all';
  if (applies === 'drinks') return isQualifyingDrinkMenuItem(item.category, item.name);
  if (applies === 'food') return isQualifyingFoodMenuItem(item.category, item.name);
  return true;
}

function itemsEligibleForOffer(
  promo: PromoDeal | null | undefined,
  items?: PromoLineItem[] | null
): PromoLineItem[] {
  const list = items || [];
  const applies = isOfferPromotion(promo) ? normalizeOfferAppliesTo(promo?.offer_applies_to) : 'all';
  if (applies === 'all') return list;
  return list.filter((item) => itemQualifiesForOffer(promo, item));
}

function merchandiseEligibleForOffer(
  promo: PromoDeal | null | undefined,
  subtotal: number,
  items?: PromoLineItem[] | null
): number {
  const cartBase = Math.max(0, subtotal);
  const applies = isOfferPromotion(promo) ? normalizeOfferAppliesTo(promo?.offer_applies_to) : 'all';
  if (applies === 'all') return cartBase;
  const scoped = itemsEligibleForOffer(promo, items).reduce((sum, item) => {
    const qty = Math.max(0, Number(item.quantity) || 0);
    const price = Math.max(
      0,
      Number(item.price ?? item.unit_price ?? item.per_unit_price) || 0
    );
    return sum + price * qty;
  }, 0);
  return Math.min(cartBase, scoped);
}

export function isBogoDeal(promo: PromoDeal | null | undefined): boolean {
  return String(promo?.deal_type || '').toLowerCase().trim() === 'bogo';
}

export function bogoOffPercent(promo: PromoDeal | null | undefined): number {
  const n = Number(promo?.bogo_off_percent);
  return n === 25 || n === 50 || n === 100 ? n : 100;
}

function unitPricesFromItems(items?: PromoLineItem[] | null): number[] {
  const units: number[] = [];
  for (const item of items || []) {
    const qty = Math.max(0, Math.round(Number(item.quantity) || 0));
    const price = Math.max(
      0,
      Number(item.price ?? item.unit_price ?? item.per_unit_price) || 0
    );
    for (let i = 0; i < qty; i += 1) units.push(price);
  }
  return units;
}

/** Pay the highest-priced drinks; discount the cheapest drink in each pair. */
export function computeBogoDiscount(
  items: PromoLineItem[] | null | undefined,
  offPercent: number
): number {
  const units = unitPricesFromItems(items)
    .filter((price) => price > 0)
    .sort((a, b) => b - a);
  const pairs = Math.floor(units.length / 2);
  if (pairs <= 0) return 0;
  const rate = Math.max(0, Math.min(100, Number(offPercent) || 0)) / 100;
  let discount = 0;
  for (let i = 0; i < pairs; i += 1) {
    discount += units[units.length - 1 - i] * rate;
  }
  return discount;
}

export function isOfferPromotion(promo: PromoDeal | null | undefined): boolean {
  return String(promo?.promotion_type || '').toLowerCase().trim() === 'offer';
}

export function isCampaignPromotion(promo: PromoDeal | null | undefined): boolean {
  return Boolean(promo) && !isOfferPromotion(promo);
}

export function normalizeCampaignAudience(
  value: string | null | undefined
): 'all' | 'new' | 'returning' {
  const key = String(value || 'all').toLowerCase().trim();
  return key === 'new' || key === 'returning' ? key : 'all';
}

export function normalizeCampaignPlacement(
  value: string | null | undefined
): 'feed' | 'homepage' {
  const key = String(value || 'feed').toLowerCase().trim();
  return key === 'homepage' ? 'homepage' : 'feed';
}

export function campaignVisibleToAudience(
  promo: PromoDeal | null | undefined,
  hasOrderedFromCafe: boolean
): boolean {
  const audience = normalizeCampaignAudience(promo?.campaign_audience);
  if (audience === 'new') return !hasOrderedFromCafe;
  if (audience === 'returning') return Boolean(hasOrderedFromCafe);
  return true;
}

function roundMoney(value: number): number {
  return Math.round(Number(value) * 100) / 100;
}

export function computePromotionDiscount(
  subtotal: number,
  promo: PromoDeal | null,
  items?: PromoLineItem[] | null
): number {
  const cartBase = Math.max(0, subtotal);
  if (!promo || cartBase <= 0) return 0;
  if (isCampaignPromotion(promo) || String(promo.deal_type || '') === 'none') return 0;
  const base = merchandiseEligibleForOffer(promo, cartBase, items);
  if (base <= 0) return 0;
  if (isBogoDeal(promo)) {
    return Math.min(
      base,
      roundMoney(computeBogoDiscount(itemsEligibleForOffer(promo, items), bogoOffPercent(promo)))
    );
  }
  const pct = Number(promo.discount_percentage || 0);
  const fixed = Number(promo.discount_amount || 0);
  if (pct > 0) return Math.min(base, roundMoney(base * (pct / 100)));
  if (fixed > 0) return Math.min(base, roundMoney(fixed));
  return 0;
}

export function formatPromotionDealLabel(promo: PromoDeal | null | undefined): string {
  if (!promo) return 'Special offer';
  const applies = isOfferPromotion(promo) ? normalizeOfferAppliesTo(promo.offer_applies_to) : 'all';
  const scope = applies === 'drinks' || applies === 'food' ? applies : '';
  if (isBogoDeal(promo)) {
    const off = bogoOffPercent(promo);
    const label = off === 100 ? 'Buy 1 get 1 free' : `Buy 1 get 1 ${off}% off`;
    return scope ? `${label} on ${scope}` : label;
  }
  const pct = Number(promo.discount_percentage || 0);
  const fixed = Number(promo.discount_amount || 0);
  if (pct > 0) return scope ? `${pct}% off ${scope}` : `${pct}% OFF`;
  if (fixed > 0) return scope ? `${fixed} JOD off ${scope}` : `${fixed} JOD OFF`;
  return scope ? `${scope[0].toUpperCase()}${scope.slice(1)} offer` : 'Special offer';
}

export function formatPromotionGuestBadge(promo: PromoDeal | null | undefined): string {
  if (!promo) return 'Special offer';
  if (isCampaignPromotion(promo)) {
    return normalizeCampaignPlacement(promo.campaign_placement) === 'homepage'
      ? 'Featured café'
      : 'Sponsored';
  }
  return formatPromotionDealLabel(promo);
}

export function formatDailyHoursLabel(promo: {
  daily_start_time?: string | null;
  daily_end_time?: string | null;
  title?: string | null;
  description?: string | null;
}): string {
  const dailyWindow = resolveDailyHours(promo);
  if (!dailyWindow) return 'All day';
  return `${dailyWindow.start}–${dailyWindow.end} (Jordan)`;
}
